import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { Command, ArrowRight, Eye, MapPin, Briefcase } from "lucide-react";
import { fmtMoney, fmtDateRelative, formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Opportunities() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${BACKEND}/api/opportunities`)
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-background" data-testid="opportunities-page">
            <Toaster position="top-right" richColors />
            <header className="sticky top-0 z-30 border-b border-border glass-nav">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/opportunities" className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-navy text-white flex items-center justify-center rounded-md">
                            <Command className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight text-sm">Closvio · Opportunities</div>
                            <div className="text-[10px] uppercase tracking-wider text-gold">Confidential · Buyer-side</div>
                        </div>
                    </Link>
                    <Link to="/login">
                        <Button variant="outline" size="sm" data-testid="broker-login-link">
                            Broker login <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                        </Button>
                    </Link>
                </div>
            </header>

            <section className="max-w-6xl mx-auto px-6 py-12">
                <div className="mb-10 animate-fade-up">
                    <div className="text-xs uppercase tracking-[0.2em] text-gold mb-3">Live opportunities</div>
                    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-none gold-underline inline-block">
                        Active mandates.
                    </h1>
                    <p className="text-sm text-muted-foreground mt-6 max-w-xl leading-relaxed">
                        Anonymised teasers from our live M&A pipeline. All figures are indicative. Sign an NDA with the mandated broker to access full information.
                    </p>
                </div>

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading opportunities…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md p-16 text-center bg-muted/30">
                        <Eye className="w-6 h-6 mx-auto text-muted-foreground mb-3 opacity-50" />
                        <p className="text-sm text-muted-foreground">No public opportunities at this time. Check back soon.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((o, i) => (
                            <motion.div
                                key={o.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.05, duration: 0.4, ease: "easeOut" }}
                                whileHover={{ y: -4 }}
                                className="group border border-border rounded-md bg-white p-5 hover:border-navy/40 hover:shadow-md transition-all"
                                data-testid={`opportunity-card-${o.id}`}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <Badge variant="outline" className="text-[10px] font-mono border-gold/50 text-gold">
                                        {o.stage}
                                    </Badge>
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{fmtDateRelative(o.created_at)}</span>
                                </div>
                                <h3 className="font-display text-lg font-bold tracking-tight mb-2 leading-tight">{o.headline}</h3>

                                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                                    {o.sector && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{o.sector}</span>}
                                    {o.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{o.location}</span>}
                                </div>

                                {o.business_description && (
                                    <p className="text-sm text-foreground/80 leading-relaxed line-clamp-3 mb-4">{o.business_description}</p>
                                )}

                                <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border">
                                    <Metric label="Revenue" value={o.revenue ? fmtMoney(o.revenue, o.currency) : "n/d"} />
                                    <Metric label="EBITDA" value={o.ebitda ? fmtMoney(o.ebitda, o.currency) : "n/d"} />
                                </div>

                                {o.asking_price > 0 && (
                                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Asking</span>
                                        <span className="font-mono text-sm font-medium text-navy">{fmtMoney(o.asking_price, o.currency)}</span>
                                    </div>
                                )}

                                <NdaRequestDialog opportunity={o} />
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>

            <footer className="border-t border-border mt-20 py-8">
                <div className="max-w-6xl mx-auto px-6 text-xs text-muted-foreground flex items-center justify-between">
                    <span>© Closvio · Strictly confidential</span>
                    <span className="font-mono text-gold">Powered by Closvio</span>
                </div>
            </footer>
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
            <div className="font-mono text-sm font-medium">{value}</div>
        </div>
    );
}

function NdaRequestDialog({ opportunity }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${BACKEND}/api/email/nda-request`, {
                deal_id: opportunity.id,
                requester_name: name,
                requester_email: email,
                message,
            });
            toast.success("Request sent — the broker will be in touch shortly.");
            setOpen(false);
            setName(""); setEmail(""); setMessage("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="w-full mt-4 bg-navy text-white hover:bg-navy/90 hover:text-white" data-testid={`request-nda-btn-${opportunity.id}`}>
                    Request NDA <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display">Request NDA access</DialogTitle>
                    <DialogDescription className="text-xs">
                        <strong className="text-foreground">{opportunity.headline}</strong>
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid={`nda-form-${opportunity.id}`}>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your name *</Label>
                        <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="nda-name" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your email *</Label>
                        <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="nda-email" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Message (optional)</Label>
                        <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Brief note about your investment thesis, firm, etc." data-testid="nda-message" />
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading} className="bg-navy text-white hover:bg-navy/90 hover:text-white" data-testid="nda-submit">
                            {loading ? "Sending…" : "Send request"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
